import React, { useCallback } from "react"

import { cn } from "@/lib/utils"

interface MagicCardProps {
  children?: React.ReactNode
  className?: string
  gradientSize?: number
  gradientColor?: string
  gradientOpacity?: number
  gradientFrom?: string
  gradientTo?: string
}

/**
 * Karte mit Lichtkegel, der dem Mauszeiger folgt. Die Position landet als CSS-Variable
 * auf dem Element; Rand und Spotlight lesen sie aus. Keine Animationsbibliothek nötig.
 */
export function MagicCard({
  children,
  className,
  gradientSize = 200,
  gradientColor = "#262626",
  gradientOpacity = 0.8,
  gradientFrom = "#9E7AFF",
  gradientTo = "#FE8BBB",
}: MagicCardProps) {
  const onPointerMove = useCallback((e: React.PointerEvent<HTMLDivElement>) => {
    const el = e.currentTarget
    const rect = el.getBoundingClientRect()
    el.style.setProperty("--mx", `${e.clientX - rect.left}px`)
    el.style.setProperty("--my", `${e.clientY - rect.top}px`)
  }, [])
  const onPointerLeave = useCallback((e: React.PointerEvent<HTMLDivElement>) => {
    e.currentTarget.style.setProperty("--mx", `${-gradientSize}px`)
    e.currentTarget.style.setProperty("--my", `${-gradientSize}px`)
  }, [gradientSize])

  return (
    <div
      className={cn(
        "group relative isolate overflow-hidden rounded-[inherit] border border-transparent",
        className
      )}
      onPointerMove={onPointerMove}
      onPointerLeave={onPointerLeave}
      style={
        {
          "--mx": `${-gradientSize}px`,
          "--my": `${-gradientSize}px`,
          background: `linear-gradient(var(--color-surface) 0 0) padding-box, radial-gradient(${gradientSize}px circle at var(--mx) var(--my), ${gradientFrom}, ${gradientTo}, var(--color-line) 100%) border-box`,
        } as React.CSSProperties
      }
    >
      <div className="bg-surface absolute inset-px z-20 rounded-[inherit]" />
      <div
        className="pointer-events-none absolute inset-px z-30 rounded-[inherit] opacity-0 transition-opacity duration-300 group-hover:opacity-(--spot-opacity)"
        style={
          {
            "--spot-opacity": gradientOpacity,
            background: `radial-gradient(${gradientSize}px circle at var(--mx) var(--my), ${gradientColor}, transparent 100%)`,
          } as React.CSSProperties
        }
      />
      <div className="relative z-40">{children}</div>
    </div>
  )
}
